import {
  HighImpactChange,
  HighImpactFieldNames,
  HighImpactObjectClass,
  MigrationObjectPlan,
} from 'config-migration/MigrationConstants';
import {ObjectActionType} from 'config-migration/types/objectActions';
import {ObjectType} from 'config-migration/types/ObjectType';

import {
  ChangelogVerb,
  MigrationChangeType,
  MigrationDiff,
  MigrationObjectChange,
  MigrationUpdate,
  SegmentType,
} from './import-changelog/MigrationChangelogConstants';
import {getUnselectedUpdateActions} from './import-plan-sections/ChildImportPlanSection';
import {OBJECT_TYPE_TO_REMOVED_CHILD_CLASS} from './ObjectSectionConfig';

/**
 * Mapping of object provenance URI to high impact changes
 */
export function getHighImpactChangeMapFromChangelog(
  changelog: Array<MigrationObjectChange>
): Map<string, Array<HighImpactChange>> {
  const result = new Map<string, Array<HighImpactChange>>();

  changelog.forEach((objectChange) => {
    objectChange.diffDetails.forEach((diff) => {
      const highImpactChange = getHighImpactChangeFromMigrationDiff(diff, objectChange);
      if (highImpactChange) {
        const provenanceUri = (diff as MigrationUpdate).provenanceUri;
        const changes = result.get(provenanceUri) || [];
        result.set(provenanceUri, [...changes, highImpactChange]);
      }
    });
  });
  return result;
}

/**
 * Return a deepcopy of the changelog with only the high impact parts of changes. Returning a deep copy allows
 * us to filter diffDetails for only high impact changes without mutating the original changelog.
 */
export function getHighImpactChangelogFromChangelog(
  changelog: Array<MigrationObjectChange>
): Array<MigrationObjectChange> {
  const newChangeLog = JSON.parse(JSON.stringify(changelog));
  return newChangeLog.filter((objectChange: MigrationObjectChange) => {
    const newDiffDetails = objectChange.diffDetails.filter(
      (diff: MigrationDiff) => getHighImpactChangeFromMigrationDiff(diff, objectChange) != null
    );
    objectChange.diffDetails = newDiffDetails;
    return newDiffDetails.length > 0;
  });
}

export function getHighImpactChangeFromMigrationDiff(
  diff: MigrationDiff,
  objectChange: MigrationObjectChange
): HighImpactChange | null {
  if (
    diff.diffType === MigrationChangeType.SUBTRACTION &&
    OBJECT_TYPE_TO_REMOVED_CHILD_CLASS.get(objectChange.objectType) === diff.objectClass
  ) {
    return HighImpactChange.REMOVED;
  }
  if (
    (diff.diffType === MigrationChangeType.SUBTRACTION || diff.diffType === MigrationChangeType.ADDITION) &&
    diff.objectClass === HighImpactObjectClass.FIELDSET_CONFIG
  ) {
    return HighImpactChange.FIELDSET;
  }

  if (diff.diffType !== MigrationChangeType.UPDATE) {
    return null;
  }
  const finalSegment = diff.diffPath[diff.diffPath.length - 1];
  if (
    finalSegment &&
    finalSegment.segmentType === SegmentType.FIELD_LABEL &&
    Object.values(HighImpactFieldNames).includes(finalSegment.segmentName as HighImpactFieldNames)
  ) {
    const fieldName = finalSegment.segmentName as HighImpactFieldNames;
    const left = diff.leftValue;
    const right = diff.rightValue;

    if (fieldName === HighImpactFieldNames.REQUIRED) {
      if (left === 'False' && right === 'True') {
        return HighImpactChange.REQUIRED;
      }
      if (left === 'True' && right === 'False') {
        return HighImpactChange.NOT_REQUIRED;
      }
    }
    if (fieldName === HighImpactFieldNames.ARCHIVED) {
      if (left === '' && right !== '') {
        return HighImpactChange.ARCHIVED;
      }
      if (left !== '' && right === '') {
        return HighImpactChange.NOT_ARCHIVED;
      }
    }
  }
  if (objectChange.objectType === ObjectType.Dropdown && diff.changelogVerb === ChangelogVerb.RENAME) {
    return HighImpactChange.DROPDOWN_UPDATE;
  }

  return null;
}

/**
 * @param plan - the migration plan for a migration object that has child objects
 * @param highImpactChangeMap - a map of object provenance URIs to high impact changes
 * @returns - the number child objects in the plan with a high impact change
 */
export function countChildHighImpactChanges(
  plan: MigrationObjectPlan,
  highImpactChangeMap: Map<string, Array<HighImpactChange>>
): number {
  const migrationChildObjectIds = plan.childMigrationPlans
    .filter(
      (childPlan) =>
        highImpactChangeMap.has(childPlan.objectId) &&
        childPlan.selectedAction.type === ObjectActionType.UPDATE
    )
    .map((childPlan) => childPlan.objectId);

  // Check unselected update actions for archived objects not found in the child migration plans.
  const allUnselectedUpdateActions = getUnselectedUpdateActions(plan.childMigrationPlans);
  const unselectedChildObjectIds = allUnselectedUpdateActions
    .map((action) => action.target.provenanceUri)
    .filter((objectId) => highImpactChangeMap.has(objectId));

  const highImpactObjectIds = new Set([...migrationChildObjectIds, ...unselectedChildObjectIds]);

  return Array.from(highImpactObjectIds).reduce(
    (total, objectId) => total + highImpactChangeMap.get(objectId)!.length,
    0
  );
}
